import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessagesSquare, Send, ShieldAlert, Loader2, Flag, Check, X, LogIn } from "lucide-react";
import { HandshakeIcon } from "../lib/icons";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../lib/AuthContext";

/**
 * LiveSupporterWall — Mongo-backed wall for the real /campaign/:id page.
 *
 * Public read (anyone). Post requires auth (donor or student). Report
 * flips a message into the admin moderation queue.
 *
 * Lighter than the demo `SupporterWall` — no session-donated hack, no
 * localStorage, no seeded data. This is the real thing.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "mra_jwt";
const MAX_LEN = 320;

const client = axios.create({ baseURL: API, withCredentials: true });
client.interceptors.request.use((cfg) => {
  const t = typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;
  if (t) {
    cfg.headers = cfg.headers || {};
    cfg.headers.Authorization = `Bearer ${t}`;
  }
  return cfg;
});

const dotColorCls = {
  tangerine: "bg-tangerine",
  cobalt:    "bg-cobalt",
  grass:     "bg-grass",
  blossom:   "bg-blossom",
  yellow:    "bg-yellow",
};

const REPORT_REASONS = [
  "Spam / off-topic",
  "Solicitation / off-platform contact",
  "Hate or harassment",
  "Adult / inappropriate content",
  "Impersonation",
  "Other",
];

const relTime = (iso) => {
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)} d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

export const LiveSupporterWall = ({ campaignId, studentFirstName, onOpenDonate, readOnly = false }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [posting, setPosting] = useState(false);
  const [reportOpenId, setReportOpenId] = useState(null);
  const [reportReason, setReportReason] = useState(REPORT_REASONS[0]);

  const refresh = useCallback(async () => {
    try {
      const res = await client.get(`/campaigns/${campaignId}/messages`);
      setMessages(res.data);
    } catch {
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => { refresh(); }, [refresh]);

  const remaining = MAX_LEN - draft.length;
  const overLimit = remaining < 0;

  const canPost = !!user;

  const submit = async () => {
    const trimmed = draft.trim();
    if (!trimmed || overLimit) return;
    setPosting(true);
    try {
      await client.post(`/campaigns/${campaignId}/messages`, { message: trimmed, anonymous });
      setDraft("");
      toast.success("Message posted", {
        description: `${studentFirstName} will see this on their campaign.`,
      });
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not post — try again.");
    } finally {
      setPosting(false);
    }
  };

  const submitReport = async (m) => {
    try {
      await client.post(`/campaigns/${campaignId}/messages/${m.id}/report`, { reason: reportReason });
      toast.success("Thanks. We'll take a look.", {
        description: "The message stays visible while our team reviews it.",
      });
      setReportOpenId(null);
      setReportReason(REPORT_REASONS[0]);
      await refresh();
    } catch {
      toast.error("Could not send report.");
    }
  };

  return (
    <div
      data-testid="live-supporter-wall"
      className="relative overflow-hidden rounded-lg border border-border bg-bone p-6 lg:p-7"
    >
      <span aria-hidden className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-blossom/25 blur-3xl" />
      <span aria-hidden className="pointer-events-none absolute -left-14 -bottom-14 h-36 w-36 rounded-full bg-cobalt/12 blur-3xl" />

      <div className="relative flex items-center gap-2.5">
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-cobalt/12">
          <MessagesSquare className="h-4 w-4 text-cobalt" strokeWidth={1.75} />
        </span>
        <div>
          <h3 className="font-serif text-3xl leading-none text-ink">Supporter wall</h3>
          <p className="text-[13px] text-olive">Notes of encouragement for {studentFirstName}.</p>
        </div>
      </div>

      {/* Composer OR sign-in prompt (hidden once the campaign has ended) */}
      {!readOnly && <div className="relative mt-5">
        {canPost ? (
          <div className="rounded-lg border border-border/60 bg-sand/60 p-4" data-testid="live-wall-composer">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={3}
              placeholder={`Leave a note for ${studentFirstName}…`}
              className="block w-full resize-none bg-transparent text-base leading-relaxed text-ink placeholder:text-olive/60 focus:outline-none"
              maxLength={MAX_LEN + 40}
              data-testid="live-wall-textarea"
            />
            <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
              <label className="inline-flex items-center gap-2 text-[13px] text-olive">
                <input
                  type="checkbox"
                  checked={anonymous}
                  onChange={(e) => setAnonymous(e.target.checked)}
                  data-testid="live-wall-anonymous"
                  className="h-3.5 w-3.5 rounded border-ink/30 text-ink focus:ring-ink/30"
                />
                Post as Anonymous Donor
              </label>
              <div className="flex items-center gap-3">
                <span
                  className={`text-[13px] tabular-nums ${overLimit ? "font-semibold text-scarlet" : "text-olive/70"}`}
                  data-testid="live-wall-counter"
                >
                  {remaining}
                </span>
                <button
                  type="button"
                  onClick={submit}
                  disabled={posting || !draft.trim() || overLimit}
                  data-testid="live-wall-submit"
                  className="inline-flex items-center gap-1.5 rounded-full bg-ink px-4 py-2 text-[13px] font-semibold uppercase tracking-wide text-sand transition hover:bg-forest disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {posting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" strokeWidth={2} />}
                  {posting ? "Posting…" : "Post message"}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div
            data-testid="live-wall-locked"
            className="flex flex-col items-start gap-3 border-t border-border pt-5 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="flex items-start gap-3">
              <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-tangerine/15">
                <ShieldAlert className="h-4 w-4 text-tangerine" strokeWidth={1.75} />
              </span>
              <div>
                <p className="text-[13px] font-semibold text-ink">Sign in to leave a note.</p>
                <p className="mt-1 text-[13px] leading-relaxed text-olive">
                  Signed-in donors and supporters can post to the wall. It only takes a moment.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <a
                href="/login"
                data-testid="live-wall-signin"
                className="inline-flex items-center gap-1.5 rounded-full bg-ink px-4 py-2 text-[13px] font-semibold uppercase tracking-wide text-sand transition hover:bg-forest"
              >
                <LogIn className="h-3 w-3" strokeWidth={2} />
                Sign in
              </a>
            </div>
          </div>
        )}
      </div>}

      {/* Wall */}
      <ul className="relative mt-5 space-y-3" data-testid="live-wall-list">
        {loading ? (
          <li className="flex items-center gap-2 text-olive">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading wall…
          </li>
        ) : messages.length === 0 ? (
          <li className="border-t border-border py-6 text-center font-serif text-base italic text-olive" data-testid="live-wall-empty">
            Be the first to send {studentFirstName} an encouraging note.
          </li>
        ) : (
          <AnimatePresence initial={false}>
            {messages.map((m) => (
              <motion.li
                key={m.id}
                layout
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className={`group flex items-start gap-3 rounded-lg border p-4 transition ${
                  m.kind === "thank_you"
                    ? "border-grass/40 bg-grass/[0.06]"
                    : m.is_flagged
                    ? "border-tangerine/30 bg-tangerine/[0.04]"
                    : "border-border/60 bg-sand/50"
                }`}
                data-testid={`live-wall-message-${m.id}`}
                data-kind={m.kind || "note"}
              >
                {m.kind === "thank_you" ? (
                  <span aria-hidden className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-grass text-sand">
                    <HandshakeIcon className="h-3.5 w-3.5" />
                  </span>
                ) : (
                  <span
                    aria-hidden
                    className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${dotColorCls[m.color] || "bg-tangerine"}`}
                  />
                )}
                <div className="min-w-0 flex-1">
                  {m.kind === "thank_you" && (
                    <p className="mb-1 text-[13px] font-semibold uppercase tracking-[0.18em] text-grass" data-testid={`live-wall-thankyou-eyebrow-${m.id}`}>
                      A thank-you from {studentFirstName} · grant received
                    </p>
                  )}
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                    <span className="font-serif text-base italic text-ink">{m.author_name}</span>
                    <span className="text-[13px] uppercase tracking-wide text-olive/70">{relTime(m.posted_at)}</span>
                    {m.is_flagged && (
                      <span className="rounded-full bg-tangerine/15 px-2 py-0.5 text-[13px] font-semibold uppercase tracking-[0.14em] text-tangerine">
                        Under review
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-base leading-relaxed text-ink/85">{m.message}</p>

                  {reportOpenId === m.id && (
                    <div className="mt-3 rounded-md border border-ink/10 bg-bone p-3" data-testid={`live-wall-report-form-${m.id}`}>
                      <p className="text-[13px] font-semibold text-ink">Why are you reporting this?</p>
                      <div className="mt-2 space-y-1.5">
                        {REPORT_REASONS.map((r) => (
                          <label key={r} className="flex items-center gap-2 text-[13px] text-ink">
                            <input
                              type="radio"
                              name={`report-${m.id}`}
                              value={r}
                              checked={reportReason === r}
                              onChange={() => setReportReason(r)}
                              className="h-3.5 w-3.5 border-ink/30 text-ink focus:ring-ink/30"
                            />
                            {r}
                          </label>
                        ))}
                      </div>
                      <div className="mt-3 flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => { setReportOpenId(null); setReportReason(REPORT_REASONS[0]); }}
                          className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-[13px] font-medium text-ink/70 hover:bg-ink/5"
                        >
                          <X className="h-3 w-3" strokeWidth={2} /> Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => submitReport(m)}
                          data-testid={`live-wall-report-submit-${m.id}`}
                          className="inline-flex items-center gap-1 rounded-full bg-ink px-3 py-1 text-[13px] font-semibold uppercase tracking-wide text-sand hover:bg-forest"
                        >
                          <Check className="h-3 w-3" strokeWidth={2} /> Send report
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {reportOpenId !== m.id && !m.is_flagged && (
                  <button
                    type="button"
                    onClick={() => setReportOpenId(m.id)}
                    aria-label="Report this message"
                    title="Report this message"
                    data-testid={`live-wall-report-${m.id}`}
                    className="rounded-full p-1.5 text-ink/40 opacity-0 transition hover:bg-ink/5 hover:text-ink group-hover:opacity-100 focus:opacity-100"
                  >
                    <Flag className="h-3.5 w-3.5" strokeWidth={1.75} />
                  </button>
                )}
              </motion.li>
            ))}
          </AnimatePresence>
        )}
      </ul>
    </div>
  );
};
